import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	inject,
	NgZone,
	OnInit,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { getPolishPaginatorIntl } from '@/common/get-polish-paginator-intl';
import { MatExpansionModule } from '@angular/material/expansion';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { InvoiceFiltersComponent } from './invoice-filters/invoice-filters.component';
import { Router } from '@angular/router';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { VindicationApiService } from '../vindication.api.service';
import { InternalStatusDto, IssuedByOption, TableInvoice } from '../vindication.model';
import { finalize } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MailArrayPipe } from '../pipes/mail-array.pipe';
import { InlineEditComponent } from '../components/inline-edit/inline-edit.component';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import type { ColumnSelectorDialogData } from './column-selector-dialog/column-selector-dialog.component';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { StatusVindicationLabelPipe } from '../pipes/status-vindication-label.pipe';

@Component({
	selector: 'app-invoice',
	standalone: true,
	imports: [
		NavbarModule,
		MatTableModule,
		DatePipe,
		MatPaginatorModule,
		MatExpansionModule,
		ReactiveFormsModule,
		MatButtonModule,
		InvoiceFiltersComponent,
		MatCheckboxModule,
		MatProgressSpinnerModule,
		MailArrayPipe,
		InlineEditComponent,
		MatSortModule,
		MatTooltipModule,
		MatDialogModule,
		StatusVindicationLabelPipe,
	],
	templateUrl: './invoice.component.html',
	styleUrl: './invoice.component.scss',
	encapsulation: ViewEncapsulation.None,
	providers: [
		{ provide: MatPaginatorIntl, useValue: getPolishPaginatorIntl() },
		{ provide: DateAdapter, useClass: PolishDateAdapter },
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceComponent implements OnInit {
	private static readonly COLUMN_KEYS = [
		'checkbox',
		'ourCompany',
		'shortName',
		'prefix',
		'nip',
		'number',
		'status',
		'internalStatus',
		'paymentTime',
		'declaredPaymentDate',
		'issueDate',
		'afterPaymentDays',
		'paymentDeadline',
		'delayDays',
		'amount',
		'vat',
		'amountWithVat',
		'currency',
		'carrierReference',
		'mail',
		'note',
		'shouldVindicate',
		'mailsSent',
		'navigatorId',
	] as const;

	readonly availableColumns = {
		checkbox: 'Checkbox',
		amount: 'Kwota',
		amountWithVat: 'Kwota z VAT',
		currency: 'Waluta',
		vat: 'VAT',
		ourCompany: 'Sprzedawca',
		shortName: 'Kontrahent',
		mail: 'Maile',
		nip: 'NIP',
		prefix: 'Prefix',
		number: 'Numer fv',
		paymentDeadline: 'Termin płatności od daty korespondencji',
		afterPaymentDays: 'Dni po terminie',
		paymentTime: 'Data zapłaty',
		declaredPaymentDate: 'Deklarowany termin płatności',
		issueDate: 'Data wystawienia',
		delayDays: 'Dni odroczenia',
		status: 'Status płatności',
		internalStatus: 'Status wewnętrzny',
		carrierReference: 'Referencja przewoźnika',
		shouldVindicate: 'Windykacja automatyczna',
		navigatorId: 'Nawigator ID',
		note: 'Notatka',
		mailsSent: 'Mail wysłany',
	} satisfies Record<(typeof InvoiceComponent.COLUMN_KEYS)[number], string>;

	readonly allColumnKeys = signal<string[]>(InvoiceComponent.COLUMN_KEYS as unknown as string[]);

	readonly visibleColumns = signal<string[]>(InvoiceComponent.COLUMN_KEYS as unknown as string[]);

	readonly displayedColumns = computed(() => {
		const visible = this.visibleColumns();
		const columns = this.allColumnKeys().filter((key) => visible.includes(key));
		return [...columns, 'mailsSentAction'];
	});

	readonly dataSource = new MatTableDataSource<TableInvoice>([]);
	readonly selection = signal<TableInvoice[]>([]);

	readonly allSelected = computed(() => {
		const selected = this.selection();
		const data = this.allCachedData();
		if (!data.length) {
			return false;
		}
		return selected.length === data.length;
	});

	readonly selectedIds = computed(() => {
		const selected = this.selection();
		return new Set(selected.map((i) => i.id));
	});

	readonly someSelectedInvoicesNotHaveMails = computed(() =>
		this.selection().some(
			(invoice) =>
				invoice.contractor === null ||
				invoice.contractor?.mails === null ||
				invoice.contractor?.mails?.length === 0,
		),
	);

	readonly length = signal(0);
	readonly pageSize = signal(10);
	readonly pageIndex = signal(0);
	readonly isLoading = signal(false);

	readonly isFiltersOpened = signal(false);
	readonly internalStatusOptions = signal<InternalStatusDto[]>([]);
	readonly issuedByOptions = signal<IssuedByOption[]>([]);

	private readonly COLUMN_STORAGE_KEY = 'invoice-table-visible-columns';
	private readonly selectedFilters = signal<string | null>(null);
	private readonly dataCache = signal<{ [key: string]: TableInvoice[] }>({});
	private readonly sortOrder = signal<Sort | null>(null);
	private readonly allCachedData = computed(() => Object.values(this.dataCache()).flat());
	private readonly router = inject(Router);
	private readonly vindicationApiService = inject(VindicationApiService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly matDialog = inject(MatDialog);
	private readonly ngZone = inject(NgZone);
	private readonly infoboxService = inject(InfoboxService);

	ngOnInit(): void {
		this.loadVisibleColumnsFromStorage();
		this.loadInternalStatuses();
		this.loadIssuedByOptions();
		this.loadInvoices();
	}

	async openColumnSelectorDialog(): Promise<void> {
		const columnSelectorComponent = await import('./column-selector-dialog/column-selector-dialog.component').then(
			(c) => c.ColumnSelectorDialogComponent,
		);

		const dialogData: ColumnSelectorDialogData = {
			availableColumns: this.availableColumns,
			allColumnKeys: this.allColumnKeys(),
			visibleColumns: this.visibleColumns(),
		};

		this.ngZone.run(() => {
			this.matDialog
				.open(columnSelectorComponent, {
					data: dialogData,
					width: '600px',
					autoFocus: false,
					restoreFocus: true,
				})
				.afterClosed()
				.subscribe((selectedColumns: string[] | undefined) => {
					if (selectedColumns) {
						const columnsToSet = selectedColumns.includes('checkbox')
							? selectedColumns
							: [...selectedColumns, 'checkbox'];
						this.visibleColumns.set(columnsToSet);
						this.saveVisibleColumnsToStorage(columnsToSet);
					}
				});
		});
	}

	onPageChange(event: PageEvent): void {
		this.pageIndex.set(event.pageIndex);

		if (this.pageSize() !== event.pageSize) {
			this.pageSize.set(event.pageSize);
			this.reloadInvoices();
			return;
		}

		this.loadInvoices();
	}

	applyFilters(filtersParams: string): void {
		this.selectedFilters.set(filtersParams);
		this.reloadInvoices();
	}

	onSortChange(sort: Sort): void {
		this.sortOrder.set(sort);
		this.reloadInvoices();
	}

	loadInvoices(): void {
		const filtersParams = this.selectedFilters();
		const cacheKey = this.getCacheKey(filtersParams);

		if (this.dataCache()[cacheKey]) {
			this.dataSource.data = this.dataCache()[cacheKey];
			return;
		}

		this.isLoading.set(true);

		const limit = this.pageSize();
		const offset = this.pageIndex() * this.pageSize();
		const sort = this.sortOrder();
		const ordering =
			sort?.direction === '' || !sort?.direction ? '' : `${sort.direction === 'desc' ? '-' : ''}${sort.active}`;
		let params = `?limit=${limit}&offset=${offset}`;
		if (ordering) {
			params += `&ordering=${ordering}`;
		}

		if (filtersParams) {
			params += filtersParams;
		}

		this.vindicationApiService
			.getInvoices(params)
			.pipe(finalize(() => this.isLoading.set(false)))
			.subscribe((invoices) => {
				this.dataSource.data = invoices.results.map((invoice) => ({
					...invoice,
					noteLoader: false,
					declaredPaymentDateLoader: false,
					internalStatusLoader: false,
				}));

				this.dataCache.set({
					...this.dataCache(),
					[cacheKey]: this.dataSource.data,
				});
				this.length.set(invoices.count);
			});
	}

	toggleAllRows(): void {
		if (this.allSelected()) {
			this.selection.set([]);
			return;
		}

		this.selection.set([...this.allCachedData()]);
	}

	toggleRow(row: TableInvoice): void {
		this.selection.update((selected) => {
			const exists = selected.includes(row);
			return exists ? selected.filter((i) => i !== row) : [...selected, row];
		});
	}

	saveNote(invoice: TableInvoice, newValue: string | string[]): void {
		if (Array.isArray(newValue)) {
			return;
		}
		invoice.noteLoader = true;
		this.vindicationApiService
			.patchInvoice(invoice.id, { note: newValue })
			.pipe(
				finalize(() => {
					invoice.noteLoader = false;
					this.cdr.detectChanges();
				}),
			)
			.subscribe((updated) => {
				invoice.note = updated.note;
			});
	}

	saveDeclaredPaymentDate(invoice: TableInvoice, newValue: string | string[]): void {
		if (Array.isArray(newValue)) {
			return;
		}
		invoice.declaredPaymentDateLoader = true;
		this.vindicationApiService
			.patchInvoice(invoice.id, { declaredPaymentDate: newValue || null })
			.pipe(
				finalize(() => {
					invoice.declaredPaymentDateLoader = false;
					this.cdr.detectChanges();
				}),
			)
			.subscribe((updated) => {
				invoice.declaredPaymentDate = updated.declaredPaymentDate;
			});
	}

	saveInternalStatus(invoice: TableInvoice, statusId: number | null): void {
		invoice.internalStatusLoader = true;
		this.vindicationApiService
			.patchInvoice(invoice.id, { internalStatus: statusId })
			.pipe(
				finalize(() => {
					invoice.internalStatusLoader = false;
					this.cdr.detectChanges();
				}),
			)
			.subscribe((updated) => {
				invoice.internalStatus = updated.internalStatus;
			});
	}

	openMailsSent(invoiceNumber: string): void {
		const urlTree = this.router.createUrlTree(['vindication/mails-sent'], {
			queryParams: { search: invoiceNumber },
		});

		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	goToMailView(): void {
		if ([...this.selectedIds()].length <= 0) {
			this.infoboxService.warning('Wybierz co najmniej jedną fakturę');
			return;
		}

		if (this.someSelectedInvoicesNotHaveMails()) {
			this.infoboxService.warning('Jeden z kontrahentów z wybranych faktur nie ma przypisanego maila');
			return;
		}

		const urlTree = this.router.createUrlTree(['vindication/invoice/mail'], {
			queryParams: {
				invoices: [...this.selectedIds()].join(','),
				sendTogetherAvailable: this.canSendTogetherSelection(),
			},
		});

		const url = this.router.serializeUrl(urlTree);
		window.open(url, '_blank');
	}

	canSendTogetherSelection(): boolean {
		const selectedContractorIds = new Set(this.selection().map((invoice) => invoice.contractor?.id));

		return selectedContractorIds.size === 1;
	}

	private loadInternalStatuses(): void {
		this.vindicationApiService.getInternalStatuses().subscribe((options) => {
			this.internalStatusOptions.set(options);
		});
	}

	private loadIssuedByOptions(): void {
		this.vindicationApiService.getInvoiceIssuedByOptions().subscribe((options) => {
			this.issuedByOptions.set(options);
		});
	}

	private loadVisibleColumnsFromStorage(): void {
		const stored = localStorage.getItem(this.COLUMN_STORAGE_KEY);
		if (stored) {
			try {
				const storedColumns = JSON.parse(stored) as string[];
				const validColumns = storedColumns.filter((col) => this.allColumnKeys().includes(col));
				const columnsToSet = validColumns.includes('checkbox') ? validColumns : [...validColumns, 'checkbox'];
				if (columnsToSet.length > 0) {
					this.visibleColumns.set(columnsToSet);
				}
			} catch {
				this.saveVisibleColumnsToStorage(this.visibleColumns());
			}
		} else {
			this.saveVisibleColumnsToStorage(this.visibleColumns());
		}
	}

	private saveVisibleColumnsToStorage(columns: string[]): void {
		localStorage.setItem(this.COLUMN_STORAGE_KEY, JSON.stringify(columns));
	}

	private getCacheKey(filtersParams: string | null): string {
		const pageIndex = this.pageIndex();
		const filtersKey = filtersParams || '';
		return `${pageIndex}_${filtersKey}`;
	}

	private reloadInvoices(): void {
		this.dataCache.set({});
		this.pageIndex.set(0);
		this.selection.set([]);
		this.loadInvoices();
	}
}
